import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
// DraggableSyntheticListeners is the public alias for `SyntheticListenerMap |
// undefined`; the map type itself is only reachable through @dnd-kit's internal
// dist/ path, which is not part of its published API.
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';

interface SortableContentItemRenderProps {
  /** Spread onto the drag-handle element together with `listeners`. */
  attributes: DraggableAttributes;
  /** Spread onto the drag-handle element to start a drag. */
  listeners: DraggableSyntheticListeners;
  isDragging: boolean;
}

interface SortableContentItemProps {
  id: string;
  disabled?: boolean;
  children: (props: SortableContentItemRenderProps) => React.ReactNode;
}

/**
 * Sortable wrapper for a content-item card. Owns the draggable root element and
 * hands the drag-handle props to `children` via a render prop, so only the grip
 * handle starts a drag (the header stays clickable for expand/collapse).
 */
export default function SortableContentItem({ id, disabled, children }: SortableContentItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : undefined,
    position: 'relative',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg overflow-hidden transition-all duration-200 hover:shadow-sm ${
        isDragging ? 'ring-2 ring-brand-blue-400' : ''
      }`}
    >
      {children({ attributes, listeners, isDragging })}
    </div>
  );
}
